<script setup lang="ts">
import { THEME_SYMBOL, useThemeService } from '../../composables/useTheme'
import { faviconToLinks, mergeFavicon } from '../../theme/utils/favicon'
import { resolveFavicon } from '../../theme/utils/resolveThemeAsset'
import { themeAssetUrls } from '#theme-assets'
import type { Name } from '#theme'
import { computed, provide, useCoreConfig, useHead } from '#imports'

export interface ThemeProviderProps {
    theme?: Name
    /**
     * When true, applies the theme class to `<body>` and injects the theme's
     * favicon `<link>` entries into `<head>`. Mount exactly one global
     * ThemeProvider per app — multiple instances call `useHead` with the
     * same keys and cause the head manager to flicker between their values.
     */
    global?: boolean
    height?: string
    width?: string
}

const props = withDefaults(defineProps<ThemeProviderProps>(), {
    height: 'initial',
    width: 'initial',
})

const { getComputedTheme } = useThemeService()

const _theme = getComputedTheme(() => props.theme)

provide(THEME_SYMBOL, {
    theme: _theme
})

const computedClass = computed(() => {
    return [`theme--${_theme.value.name}`, `${_theme.value.dark ? 'theme--dark' : 'theme--light'}`]
})

if (props.global) {
    const { site } = useCoreConfig()
    // Inlined rather than calling `useThemeFavicon()` because Vue's
    // `inject` doesn't see the current component's own `provide()` —
    // `useTheme()` would return the parent's theme, not `_theme`.
    const faviconLinks = computed(() => faviconToLinks(
        resolveFavicon(mergeFavicon(_theme.value.favicon, site.favicon), themeAssetUrls),
    ))

    useHead({
        bodyAttrs: {
            class: () => computedClass.value?.join(' ')
        },
        link: faviconLinks,
    })
}
</script>

<template>
    <div
        class="theme-provider"
        :class="props.global ? undefined : computedClass"
        :style="{ height: props.height, width: props.width }"
    >
        <UIDefaultsProvider v-if="_theme.defaults" :defaults="_theme.defaults">
            <slot />
        </UIDefaultsProvider>
        <slot v-else />
    </div>
</template>
